import React from 'react';
import '../css/ccldatatable.css';
import ButtonConfirm, { ButtonConfirmSize } from "./ButtonConfirm";

const sortAscIcon = require('../images/sort-ascending.png');
const sortDescIcon = require('../images/sort-descending.png');

interface ICclDataTable {
  name: string,
  className?: string,
  tableData: any[],
  columnDefinitions: CclDataTableColumnDef[],
  rowKeyFields?: string[] | null,
  rowSelect: boolean,
  defaultSelectedRow?: any,
  sortable?: boolean,
  onRowSelect?: ((rowData: any) => void) | null,
  onButtonClick?: ((rowData: any, buttonName: string) => void) | null
}

export enum CclDataTableColumnType {
  Data,
  Button,
  Checkbox
}

export enum CclDataTableButtonType {
  Standard,
  Delete,
  OK,
  Confirm
}

export class CclDataTableColumnDef {
  fieldName: string = "";
  title: string = "";
  visible: boolean = true;
  type: CclDataTableColumnType = CclDataTableColumnType.Data;
  formatData?: ((val: any, rowData: any) => void) | undefined = undefined;
  className?: string | undefined = undefined;
  buttonName?: string | undefined = undefined;
  buttonText?: string | undefined = undefined;
  buttonType?: CclDataTableButtonType | undefined = undefined;
  buttonConfirmText?: string | undefined = undefined;
  buttonConfirmTitle?: string | undefined = undefined;
  buttonClassName?: string | undefined = undefined;
  editable?: ((rowData: any) => boolean) | boolean | undefined = undefined;
  sortable?: boolean = undefined;
}

enum ColumnSortOrder {
  None,
  Ascending,
  Descending
}

export default function CclDataTable({ name, className, tableData, columnDefinitions, rowKeyFields, rowSelect, defaultSelectedRow,
  onRowSelect, onButtonClick, sortable }: ICclDataTable) {

  const [selectedRow, setSelectedRow] = React.useState<any>(defaultSelectedRow);
  const [sortColumn, setSortColumn] = React.useState<string>("");
  const [currentColumnSortOrder, setCurrentColumnSortOrder] = React.useState<ColumnSortOrder>(ColumnSortOrder.None);
  const [sortedData, setSortedData] = React.useState<any[] | null>(null);

  React.useEffect(() => {
    setSortedData(tableData);
  }, [tableData]);

  React.useEffect(() => {
    if (!sortColumn || currentColumnSortOrder === ColumnSortOrder.None) {
      setSortedData(tableData);
    } else {
      var sorted = sortByField(tableData, sortColumn, currentColumnSortOrder == ColumnSortOrder.Ascending)
      setSortedData(sorted);
    }
  }, [sortColumn, currentColumnSortOrder]);

  function sortByField<T>(array: T[], field: keyof T, ascending: boolean = true): T[] {
    // Create a shallow copy of the array to avoid modifying the original
    const sortedArray = [...array];

    sortedArray.sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];

      // Handle different types for comparison
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return ascending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === 'number' && typeof valueB === 'number') {
        return ascending ? valueA - valueB : valueB - valueA;
      } else {
        // Fallback for other types or mixed types (e.g., boolean, undefined, null)
        // You might want to add more specific handling here based on expected data.
        if (valueA < valueB) {
          return ascending ? -1 : 1;
        }
        if (valueA > valueB) {
          return ascending ? 1 : -1;
        }
        return 0; // Values are equal
      }
    });

    return sortedArray;
  }

  function handleRowClick(rowData: any) {
    if (rowSelect) {
      setSelectedRow(rowData);
      if (onRowSelect) {
        onRowSelect(rowData);
      }
    }
  }

  function buildDataColumn(rowData: any, rownum: number, colData: CclDataTableColumnDef) {
    let val = rowData[colData.fieldName];
    if (colData.formatData) {
      val = colData.formatData(val, rowData);
    }
    return (
      <React.Fragment key={"dc" + rownum + (colData.title.replace(" ", "_") || rownum)}>
        {colData.visible && <td className={colData.className}>{val}</td>}
      </React.Fragment>
    )
  }

  function buildEditColumn(rowData: any, rownum: number, colData: CclDataTableColumnDef) {
    let val = rowData[colData.fieldName];
    if (colData.formatData) {
      val = colData.formatData(val, rowData);
    }
    return (
      <React.Fragment key={"dc" + rownum + (colData.title.replace(" ", "_") || rownum)}>
        {colData.visible && <td>
          <input
            className="form-control"
            id={`i${rownum}${(colData.title.replace(" ", "_") || rownum)}`}
            autoComplete="off"
            defaultValue={val || ""}
            onChange={(e) => { rowData[colData.fieldName] = e.target.value; }} />
        </td>}
      </React.Fragment>
    )
  }

  function handleButtonClick(rowData: any, buttonName: string) {
    if (onButtonClick) {
      onButtonClick(rowData, buttonName);
    }
  }

  function buildButtonColumn(rowData: any, rownum: number, colData: CclDataTableColumnDef) {
    let buttonClasses = "btn btn-primary btn-sm";
    if (colData.buttonType === CclDataTableButtonType.Delete) {
      buttonClasses = "btn btn-danger btn-sm";
    } else if (colData.buttonType === CclDataTableButtonType.OK) {
      buttonClasses = "btn btn-success btn-sm";
    }

    return (
      <React.Fragment key={"bc" + rownum + (colData.title.replace(" ", "_") || rownum)}>
        {colData.visible &&
          <td>
            {colData.buttonType === CclDataTableButtonType.Confirm &&
              <ButtonConfirm enabled={true} buttonText={colData.buttonText || "Confirm"} confirmTitle={colData.buttonConfirmTitle || "Confirm"}
                confirmMessage={colData.buttonConfirmText || "Confirm?"}
                buttonSize={ButtonConfirmSize.Normal} cssClass={colData.buttonClassName || buttonClasses}
                onConfirm={(c) => { if (c) { handleButtonClick(rowData, colData.buttonName || "Confirm"); } }} />
            }
            {colData.buttonType !== CclDataTableButtonType.Confirm &&
              <button onClick={() => handleButtonClick(rowData, colData.buttonName || "OK")}
                className={colData.buttonClassName || buttonClasses}>{colData.buttonText}</button>}
          </td>}
      </React.Fragment>
    )
  }

  function buildCheckboxColumn(rowData: any, rownum: number, colData: CclDataTableColumnDef) {
    let val = rowData[colData.fieldName];
    return (
      <React.Fragment key={"cbx" + rownum + (colData.title.replace(" ", "_") || rownum)}>
        {colData.visible &&
          <td className={colData.className}>
            <input type="checkbox" readOnly checked={val} id={`i${rownum}${(colData.title.replace(" ", "_") || rownum)}`} />
          </td>}
      </React.Fragment>
    )
  }

  function getRowKey(rowData: any, index: number): string {
    if (!rowKeyFields || rowKeyFields.length === 0) {
      return `${name}row${index}`;
    }
    let key = "";
    rowKeyFields.map(fieldName => {
      var val = rowData[fieldName];
      key = `${key}${index}${val}`;
    });

    return `${name}row${key}`;
  }

  function buildRow(rowData: any, index: number) {
    const selectedClass = (rowSelect && rowData === selectedRow) ? " selected" : "";
    const rowClass = `${index % 2 === 0 ? "ccl-datarow shaded" : "ccl-datarow"} ${selectedClass}`;
    const key = getRowKey(rowData, index);

    return (
      <tr key={key} data-item={rowData} className={rowClass} onClick={() => handleRowClick(rowData)}>
        {columnDefinitions && columnDefinitions.map((colData: CclDataTableColumnDef) => {
          if (colData.type === CclDataTableColumnType.Button) {
            return buildButtonColumn(rowData, index, colData);
          } else if (colData.type === CclDataTableColumnType.Checkbox) {
            return buildCheckboxColumn(rowData, index, colData);
          } else if (checkIsEditable(rowData, colData.editable)) {
            return buildEditColumn(rowData, index, colData);
          } else {
            return buildDataColumn(rowData, index, colData);
          }
        })}
      </tr>)
  }

  function isFunction(functionToCheck: any) {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
  }

  function checkIsEditable(rowData: any, valueOrFunction: any): boolean {
    if (isFunction(valueOrFunction)) {
      return valueOrFunction(rowData);
    } else {
      return !!valueOrFunction;
    }
  }

  function columnSortClick(colData: CclDataTableColumnDef) {
    if (sortable && colData.sortable) {
      if (colData.fieldName === sortColumn) {
        switch (currentColumnSortOrder) {
          case ColumnSortOrder.None:
            setCurrentColumnSortOrder(ColumnSortOrder.Ascending);
            break;
          case ColumnSortOrder.Ascending:
            setCurrentColumnSortOrder(ColumnSortOrder.Descending);
            break;
          case ColumnSortOrder.Descending:
          default:
            setCurrentColumnSortOrder(ColumnSortOrder.None);
            break;
        }
      } else {
        setCurrentColumnSortOrder(ColumnSortOrder.Ascending);
        setSortColumn(colData.fieldName);
      }
    }
  }

  function buildHeaderColumn(colData: CclDataTableColumnDef, index: number) {
    let getSortIcon = () => {
      if (sortable && colData.sortable) {
        if (!!sortColumn && currentColumnSortOrder === ColumnSortOrder.Ascending) {
          return (
            <img src={sortAscIcon} alt="Sort Ascending" className="" />
          )
        } else if (!!sortColumn && currentColumnSortOrder === ColumnSortOrder.Descending) {
          return (
            <img src={sortDescIcon} alt="Sort Descending" className="" />
          )
        }
      }
    };
    return (
      <React.Fragment key={name + "hc" + index}>
        {colData.visible && <td className={"ccl-table-header-col " + (colData.className || "") + (colData.sortable ? " sortable" : "")}
          onClick={() => columnSortClick(colData)}>{colData.title} {getSortIcon()}</td>}
      </React.Fragment>
    )
  }

  function buildRows() {
    if (!sortedData) return;

    return sortedData.map((rowData: any, index: number) => {
      return buildRow(rowData, index);
    });
  }

  return (
    <table className={`ccl-data-table ${className || ""}`}>
      <thead>
        <tr key={name + "_header_1"} className="ccl-table-header-row">
          {columnDefinitions && columnDefinitions.map((colData: CclDataTableColumnDef, index: number) => {
            return buildHeaderColumn(colData, index);
          })}
        </tr>
      </thead>
      <tbody className="ccl-table-body">
        {!!sortedData && buildRows()}
      </tbody>
    </table>
  )
}